<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomRadio, AtomText, AtomButton } from "@chatfood/design-system";
import { ref, watch } from "vue";
import type { IDomainCustomerAddress } from "~/domain/customer-address";
import { useCustomerStore } from "~/stores/customer";
import type { IDomainCustomerCoveredAddress } from "~/domain/customer-covered-address";
import type { IAddressBookProps } from "~/components/address-book/props";
import { useModalHubStore } from "~/components/modal-hub";
import { useLocale } from "~/composables/use-locale";
import { useFeatureMatrixStore } from "#imports";

const props = defineProps<IAddressBookProps>();
const emits = defineEmits<{
  selected: [address: IDomainCustomerCoveredAddress];
  deliver: [address: IDomainCustomerCoveredAddress];
}>();

const css = bemBuilder("address-book");

const customer = useCustomerStore();
const featureMatrix = useFeatureMatrixStore();
const modalHub = useModalHubStore();

const { t } = useLocale();

const findCoveredAddressById = (id: string): IDomainCustomerCoveredAddress => {
  const address: IDomainCustomerAddress | undefined = customer.addresses.find(
    (a) => a.id === id && a.canDeliver,
  );

  if (!address) {
    throw new Error("Selected address id not found");
  }

  if (!address.canDeliver) {
    throw new Error("Selected address id has no coverage");
  }

  return address;
};

const selectedAddressId = ref<string | undefined>(props.activeAddressId);
const onSelectAddress = (value: string) => {
  selectedAddressId.value = value;
  const address = findCoveredAddressById(value);

  emits("selected", address);
};

watch(
  () => props.activeAddressId,
  (val) => {
    selectedAddressId.value = val;
  },
);

const onDeliver = () => {
  if (!selectedAddressId.value) {
    throw new Error("Deliver request with no address selected.");
  }

  const address = findCoveredAddressById(selectedAddressId.value);

  emits("deliver", address);
};
</script>

<template>
  <div :class="css('')">
    <AtomText type="subtitle">
      {{ t("components.address_book.title") }}
    </AtomText>
    <div
      v-if="customer.addresses.length"
      :class="css('addresses')"
      data-test="customer-addresses"
    >
      <div
        v-for="address in customer.addresses"
        :key="address.id"
        data-test="address-item"
      >
        <AtomRadio
          :key="address.id"
          name="address-book"
          :disabled="!address.canDeliver"
          :checked="selectedAddressId === address.id"
          :value="address.id"
          @change="onSelectAddress"
        >
          <div :class="css('item-content')">
            <AtomText
              type="body-bold"
              data-test="address-can-deliver"
              :color="
                address.canDeliver ? 'color-text-body' : 'color-text-disabled'
              "
            >
              <template v-if="!address.canDeliver">
                {{
                  t("components.address_book.uncovered_address_title", {
                    address: address.nickname,
                  })
                }}
              </template>
              <template v-else>{{ address.nickname }}</template>
            </AtomText>
            <AtomText
              data-test="address-location"
              :color="
                address.canDeliver
                  ? 'color-text-secondary'
                  : 'color-text-disabled'
              "
            >
              {{ address.location }}
            </AtomText>
          </div>
        </AtomRadio>
        <AtomButton
          v-if="selectedAddressId === address.id && !props.hideDeliverCta"
          :is-loading="props.isCtaLoading"
          :disabled="props.isCtaDisabled"
          :class="css('cta')"
          size="medium"
          data-test="address-cta"
          @click="onDeliver"
        >
          {{ props.ctaLabel ?? t("components.address_book.select_cta") }}
        </AtomButton>
      </div>
    </div>
    <AtomText v-else data-test="no-addresses">
      {{ t("components.address_book.no_address_available") }}
    </AtomText>
    <AtomButton
      v-if="!customer.isLogged && !featureMatrix.skipAuthFlow"
      size="medium"
      variant="secondary"
      theme="tailored"
      data-test="login-cta"
      @click="() => modalHub.showAuthManager()"
    >
      {{ t("components.address_book.login_cta") }}
    </AtomButton>
  </div>
</template>

<style lang="scss" scoped>
.address-book {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  &__addresses {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  &__cta {
    margin-left: var(--spacing-xl);
    margin-top: var(--spacing-sm);
  }

  &__item-content {
    display: flex;
    flex-direction: column;
  }
}
</style>
